import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import LogoOneDoor from "@shared/components/LogoOneDoor";
import CustomerBranding from "@shared/components/CustomerBranding";
import LanguageDropdown from "@shared/components/LanguageDropdown";
import Footer from "@shared/components/Footer";
import SSOLoginCard from "@shared/features/auth/components/SSOLoginCard";

import Dashboard from "./pages/Dashboard";
import OnboardingProvisioningPage from "./pages/OnboardingProvisioningPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";

import getCustomerFromSubdomain from "@shared/features/branding/utils/getCustomerFromSubdomain";
import getCustomerKeyFromUrl from "@shared/features/branding/utils/getCustomerKeyFromUrl";
import customerEnvLabelHint from "@shared/features/branding/utils/customerEnvLabelHint";
import fetchCustomerBranding, { getStoredBrand } from "@shared/api/fetchCustomerBranding";
import defaultCustomerLogo from "@shared/assets/odp.png";
import { checkAuthStatus, clearStoredTokens } from "@shared/utils/localStorage";
import { broadcastAuthLogout, subscribeAuthLogout } from "@shared/utils/authBroadcast";
import { clearAuthCookies } from "@shared/utils/cookies";
import { getAuthHeaders } from "@shared/utils/api";
import { apiUrl } from "@shared/utils/api";
import {
  replaceLocationStrippingAuthCallbackParams,
  sanitizeUrlStrippingAuthCallbackParams,
} from "@shared/utils/authCallbackUrl";
import {
  isLikelyCustomerProductAppHost,
  isMainCustomerPortalShell,
} from "@shared/utils/customerPortalDocument";

const LAST_LAUNCH_URL_KEY = "odp_last_launch_url";

function normalizeAllowedReturnHostEntry(entry) {
  let value = String(entry || "").trim().toLowerCase();
  if (!value) return null;

  if (value.startsWith("http://") || value.startsWith("https://")) {
    try {
      value = new URL(value).hostname.toLowerCase();
    } catch {
      return null;
    }
  }

  if (value.startsWith("*.")) value = `.${value.slice(2)}`;
  const hostWithPortMatch = value.match(/^([^:]+):\d+$/);
  if (hostWithPortMatch) value = hostWithPortMatch[1];

  return value || null;
}

function allowedReturnHostsFromRuntimeEnv() {
  const runtimeEnv = typeof window !== "undefined" ? window.ENV : null;
  const raw = runtimeEnv?.ALLOWED_RETURN_URL_HOSTS;
  if (Array.isArray(raw)) {
    return raw
      .map((entry) => normalizeAllowedReturnHostEntry(entry))
      .filter(Boolean);
  }
  return String(raw || "")
    .split(",")
    .map((entry) => normalizeAllowedReturnHostEntry(entry))
    .filter(Boolean);
}

function hostAllowedByRuntimeEnv(hostname) {
  const host = String(hostname || "").toLowerCase();
  if (!host) return false;
  const allowedHosts = allowedReturnHostsFromRuntimeEnv();
  if (allowedHosts.length === 0) return false;
  for (const entry of allowedHosts) {
    if (entry.startsWith(".")) {
      if (host === entry.slice(1) || host.endsWith(entry)) return true;
    } else if (host === entry) {
      return true;
    }
  }
  return false;
}

function parseSafeOriginalHost(rawValue) {
  const decoded = decodeURIComponent(String(rawValue || "").trim()).toLowerCase();
  if (!decoded) return "";
  if (!/^[a-z0-9.-]+(?::\d+)?$/.test(decoded)) return "";
  const hostOnly = decoded.split(":")[0] || "";
  if (!hostOnly.endsWith(".localhost")) return "";
  return decoded;
}

function parseSafeReturnTo(rawValue) {
  const decoded = decodeURIComponent(String(rawValue || "").trim());
  if (!decoded) return "";
  try {
    const parsed = new URL(decoded);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return "";
    if (!hostAllowedByRuntimeEnv(parsed.hostname)) return "";
    return parsed.toString();
  } catch {
    return "";
  }
}

function clearLogoutReturnToCookie() {
  const expired = "logout_return_to=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0";
  document.cookie = expired;
  document.cookie = `${expired}; domain=localhost`;
  document.cookie = `${expired}; domain=.localhost`;
}

function singleSegmentPath(pathname) {
  const segment = String(pathname || "").replace(/^\/+|\/+$/g, "");
  if (!segment || segment.includes("/")) return "";
  return segment;
}

function isReservedHostPathSegment(segment) {
  const s = String(segment || "").toLowerCase();
  return s === "accept-invite" || s === "auth" || s === "customer";
}

export default function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [launchRedirecting, setLaunchRedirecting] = useState(false);
  const [hostPathLaunchUnavailable, setHostPathLaunchUnavailable] = useState(false);
  const [_userData, setUserData] = useState(null);

  const customerFromPath = useMemo(() => {
    const path = getCustomerFromSubdomain();
    return path;
  }, []);
  const customerKey = useMemo(() => {
    const key = getCustomerKeyFromUrl({ customerFromPath });
    return key;
  }, [customerFromPath]);

  const logoutTriggeredRef = useRef(false);
  const [brand, setBrand] = useState(() => {
    const stored = getStoredBrand();
    return stored || { name: "", logoSrc: "", alt: "Loading", product_alias: "" };
  });
  const customerLogoSrc = brand.logoSrc;
  const [language, setLanguage] = useState("en");
  const isOnboardingRoute = useMemo(() => {
    if (typeof window === "undefined") return false;
    return /\/onboard\/?$/.test(window.location.pathname || "");
  }, []);
  const isAcceptInviteRoute = useMemo(() => {
    if (typeof window === "undefined") return false;
    return /^\/accept-invite\/?$/i.test((window.location.pathname || "").replace(/\/+$/, "") || "");
  }, []);
  const launchPathSegment = useMemo(() => {
    if (typeof window === "undefined") return "";
    return singleSegmentPath(window.location.pathname || "/");
  }, []);
  const isHostPathLaunchRoute = Boolean(
    launchPathSegment &&
    launchPathSegment !== "logout" &&
    !isOnboardingRoute &&
    !isAcceptInviteRoute &&
    !isReservedHostPathSegment(launchPathSegment)
  );
  const sameOriginReturnUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const returnUrlRaw = new URLSearchParams(window.location.search).get("return_url");
    const safeReturnUrl = parseSafeReturnTo(returnUrlRaw);
    if (!safeReturnUrl) return "";
    try {
      const parsed = new URL(safeReturnUrl, window.location.origin);
      if (parsed.origin !== window.location.origin) return "";
      const segment = singleSegmentPath(parsed.pathname || "/");
      if (!segment || segment === "logout") return "";
      parsed.searchParams.delete("return_url");
      return parsed.toString();
    } catch {
      return "";
    }
  }, []);
  const sameOriginReturnPathSegment = useMemo(() => {
    if (!sameOriginReturnUrl) return "";
    try {
      return singleSegmentPath(new URL(sameOriginReturnUrl).pathname || "/");
    } catch {
      return "";
    }
  }, [sameOriginReturnUrl]);
  const externalReturnUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const returnUrlRaw = new URLSearchParams(window.location.search).get("return_url");
    const safeReturnUrl = parseSafeReturnTo(returnUrlRaw);
    if (!safeReturnUrl) return "";
    try {
      if (new URL(safeReturnUrl, window.location.origin).origin === window.location.origin) return "";
    } catch {
      return "";
    }
    return safeReturnUrl;
  }, []);
  const [externalReturnRedirectFailed, setExternalReturnRedirectFailed] = useState(false);
  const [sameOriginReturnRedirectFailed] = useState(false);
  const sameOriginReturnActive = Boolean(
    sameOriginReturnPathSegment &&
    sameOriginReturnPathSegment !== launchPathSegment &&
    !sameOriginReturnRedirectFailed
  );

  function handleLogin() {
    const getAuthToken = async () => {
      try {
        const response = await fetch(apiUrl("/api/v1/auth/login"), {
          credentials: 'include'
        });

        if (response.ok) {
          const tokenData = await response.json();
          localStorage.setItem('auth_token', tokenData.token);
          localStorage.setItem('token_expires', Date.now() + (tokenData.expires_in * 1000));
        }
      } catch {
        // Non-fatal: allow login flow to continue without a cached token.
      }
    };

    const checkAuth = async () => {
      try {
        const authData = await checkAuthStatus();
        setIsLoggedIn(authData.authenticated);

        if (authData.authenticated && authData.user) {
          sessionStorage.removeItem('force_reauth');
          await getAuthToken();

          setUserData(authData.user);
          setLoggedInUser({
            name: authData.user.name || authData.user.email || "User",
            email: authData.user.email,
            role: authData.user.role || "",
            rolesDisplay: authData.user.roles_display || authData.user.rolesDisplay || [],
            apps: authData.user.apps || [],
            appsDisplay: authData.user.apps_display || authData.user.appsDisplay || [],
            can_manage_customer_users: authData.user.can_manage_customer_users === true
          });
        }
      } catch {
        // Non-fatal: auth state will be re-evaluated on the next check.
      }
    };

    checkAuth();
  }

  const performLocalLogout = useCallback(async () => {
    if (logoutTriggeredRef.current) return;
    logoutTriggeredRef.current = true;
    broadcastAuthLogout();
    let logoutRedirectUrl = null;
    const returnUrlFromQuery = new URLSearchParams(window.location.search).get("return_url");
    const safeReturnUrl = returnUrlFromQuery ? parseSafeReturnTo(returnUrlFromQuery) : "";
    const onPortalShell = isMainCustomerPortalShell({
      hostname: window.location.hostname,
      pathname: window.location.pathname,
      isOnboardingRoute,
      isAcceptInviteRoute,
    });
    const productAppHost = isLikelyCustomerProductAppHost(window.location.hostname);
    // Per-tab return target: each document tells the API where *this* tab lives. Portal dashboard must
    // not reuse ?return_url= (stale or from another flow); product hosts use this tab's URL only.
    let rawLogoutReturn;
    if (onPortalShell) {
      rawLogoutReturn = `${window.location.origin}/`;
    } else if (productAppHost) {
      rawLogoutReturn = window.location.href;
    } else {
      rawLogoutReturn = safeReturnUrl || window.location.href;
    }
    const logoutReturnTo = sanitizeUrlStrippingAuthCallbackParams(rawLogoutReturn);

    const authToken = localStorage.getItem('auth_token');
    const headers = {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      "X-Original-Host": window.location.host,
      "Content-Type": "application/json",
    };
    const logoutUrl = `${window.location.origin}/api/v1/auth/logout`;

    const invokeLogout = async () => {
      const response = await fetch(logoutUrl, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ return_to: logoutReturnTo }),
      });

      if (response.ok && !logoutRedirectUrl) {
        const data = await response.json().catch(() => ({}));
        logoutRedirectUrl = String(data.redirect_url || data.customer_portal_url || "").trim() || null;
      }
    };

    try {
      await invokeLogout();
    } catch {
      // Best-effort: local cleanup should proceed even if server logout fails.
    }

    clearStoredTokens();
    sessionStorage.clear();
    sessionStorage.setItem('force_reauth', 'true');

    clearAuthCookies();

    localStorage.removeItem('customer_data');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_name');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('token_expires');
    localStorage.removeItem(LAST_LAUNCH_URL_KEY);

    setIsLoggedIn(false);
    setLoggedInUser(null);

    let redirectTo = logoutRedirectUrl;
    if (!redirectTo) {
      const origin = window.location.origin;
      if (onPortalShell) {
        redirectTo = `${origin}/`;
      } else {
        const target = new URL(`${origin}/`);
        if (logoutReturnTo) {
          try {
            target.searchParams.set("return_url", logoutReturnTo);
          } catch {
            // ignore invalid URL edge cases
          }
        }
        redirectTo = target.toString();
      }
    }
    window.location.href = redirectTo;
  }, [isAcceptInviteRoute, isOnboardingRoute]);

  async function handleLogout() {
    await performLocalLogout();
  }

  useEffect(() => {
    const checkAuth = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const hasAuthCallback = Boolean(urlParams.get("token") || urlParams.get("code"));

      if ((window.location.hostname === 'localhost' || window.location.hostname === "127.0.0.1") && !hasAuthCallback) {
        const hashParams = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
        const returnToFromHash = parseSafeReturnTo(hashParams.get("logout_return_to"));
        if (returnToFromHash) {
          window.location.replace(returnToFromHash);
          return;
        }

        const returnToFromQuery = parseSafeReturnTo(new URLSearchParams(window.location.search).get("logout_return_to"));
        if (returnToFromQuery) {
          window.location.replace(returnToFromQuery);
          return;
        }

        const returnToUrl = parseSafeReturnTo(document.cookie
          .split('; ')
          .find(row => row.startsWith('logout_return_to='))
          ?.split('=')[1]);
        if (returnToUrl) {
          clearLogoutReturnToCookie();
          window.location.replace(returnToUrl);
          return;
        }

        const returnToFromReferrer = parseSafeReturnTo(document.referrer);
        if (returnToFromReferrer) {
          const parsedReferrer = new URL(returnToFromReferrer);
          // Avoid redirect loop: only navigate away for a different origin; otherwise continue auth check.
          if (parsedReferrer.origin !== window.location.origin) {
            window.location.replace(`${parsedReferrer.origin}/`);
            return;
          }
        }

        const originalHostname = parseSafeOriginalHost(document.cookie
          .split('; ')
          .find(row => row.startsWith('original_hostname='))
          ?.split('=')[1]);

        if (originalHostname) {
          const protocol = window.location.protocol;
          const redirectUrl = `${protocol}//${originalHostname}${window.location.pathname}${window.location.search}`;
          window.location.replace(redirectUrl);
          return;
        }
      }

      if (isOnboardingRoute) {
        setAuthChecked(true);
        return;
      }
      if (/^\/accept-invite\/?$/i.test((window.location.pathname || "").replace(/\/+$/, ""))) {
        setAuthChecked(true);
        return;
      }

      if (window.location.pathname === '/logout') {
        void performLocalLogout();
        return;
      }

      let sessionEstablished = false;

      let existingToken = localStorage.getItem('auth_token');

      const urlCode = urlParams.get("code");
      const urlToken = urlParams.get("token");

      if (urlCode) {
        if (!existingToken) {
          try {
            const codeRes = await fetch(apiUrl("/api/v1/auth/token_from_code"), {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ code: urlCode }),
            });
            if (codeRes.ok) {
              const data = await codeRes.json();
              const token = data.token;
              if (token) {
                localStorage.setItem("auth_token", token);
                localStorage.setItem("token_expires", Date.now() + (data.expires_in || 3600) * 1000);
                existingToken = token;
                sessionEstablished = true;
              }
            }
          } catch {
            // Code exchange failed; continue with normal auth check (will show login if no other auth).
          }
        }
        replaceLocationStrippingAuthCallbackParams();
      }

      if (urlToken) {
        try {
          const validateResponse = await fetch(apiUrl("/api/v1/auth/validate_auth_token"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ auth_token: urlToken }),
            credentials: "include",
          });

          if (validateResponse.ok) {
            await validateResponse.json();
            localStorage.setItem("auth_token", urlToken);
            localStorage.setItem("token_expires", Date.now() + 30 * 60 * 1000);
            existingToken = urlToken;
            sessionEstablished = true;
            replaceLocationStrippingAuthCallbackParams();
          } else {
            replaceLocationStrippingAuthCallbackParams();
          }
        } catch {
          replaceLocationStrippingAuthCallbackParams();
        }
      }

      if (!existingToken) {
        const cookieToken = document.cookie
          .split('; ')
          .find(row => row.startsWith('auth_token='))
          ?.split('=')[1];

        if (cookieToken) {
          existingToken = decodeURIComponent(cookieToken);
          try {
            const validateResponse = await fetch(apiUrl("/api/v1/auth/validate_auth_token"), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ auth_token: existingToken }),
              credentials: 'include'
            });

            if (validateResponse.ok) {
              await validateResponse.json();
              localStorage.setItem('auth_token', existingToken);
              localStorage.setItem('token_expires', Date.now() + (30 * 60 * 1000));
              sessionEstablished = true;
            } else {
              existingToken = null;
            }
          } catch {
            existingToken = null;
          }

          document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.localhost';
          const hostParts = window.location.hostname.split('.');
          if (hostParts.length >= 2) {
            const baseDomain = '.' + hostParts.slice(-2).join('.');
            document.cookie = `auth_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${baseDomain}`;
          }
        }
      }

      if (existingToken && !sessionEstablished) {
        try {
          const validateResponse = await fetch(apiUrl("/api/v1/auth/validate_auth_token"), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ auth_token: existingToken }),
            credentials: 'include'
          });

          if (validateResponse.ok) {
            sessionEstablished = true;
          } else {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('token_expires');
            existingToken = null;
          }
        } catch {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('token_expires');
          existingToken = null;
        }
      }

      try {
        const authData = await checkAuthStatus();
        const forceReauthPending = sessionStorage.getItem("force_reauth") === "true";
        const callbackTokenPresent = Boolean(urlToken || urlParams.get("code"));
        const shouldBlockAutoAuth = forceReauthPending && !callbackTokenPresent;
        if (shouldBlockAutoAuth) {
          setIsLoggedIn(false);
          setUserData(null);
          setLoggedInUser(null);
          setAuthChecked(true);
          return;
        }

        setIsLoggedIn(authData.authenticated);

        if (authData.authenticated && authData.user) {
          sessionStorage.removeItem('force_reauth');
          setHostPathLaunchUnavailable(false);
          setLaunchRedirecting(
            isHostPathLaunchRoute
            || (!!externalReturnUrl && !externalReturnRedirectFailed)
            || sameOriginReturnActive
          );
          if (!localStorage.getItem('auth_token')) {
            try {
              const tokenResponse = await fetch(apiUrl("/api/v1/auth/login"), {
                credentials: 'include'
              });
              if (tokenResponse.ok) {
                const tokenData = await tokenResponse.json();
                localStorage.setItem('auth_token', tokenData.token);
                localStorage.setItem('token_expires', Date.now() + (tokenData.expires_in * 1000));
              }
            } catch {
              // Token fetch failure should not block the authenticated session.
            }
          }

          setUserData(authData.user);
          setLoggedInUser({
            name: authData.user.name || authData.user.email || "User",
            email: authData.user.email,
            role: authData.user.role || "",
            rolesDisplay: authData.user.roles_display || authData.user.rolesDisplay || [],
            apps: authData.user.apps || [],
            appsDisplay: authData.user.apps_display || authData.user.appsDisplay || [],
            can_manage_customer_users: authData.user.can_manage_customer_users === true
          });
        } else {
          setHostPathLaunchUnavailable(false);
          setLaunchRedirecting(false);
          setUserData(null);
          setLoggedInUser(null);
        }
      } catch {
        setHostPathLaunchUnavailable(false);
        setLaunchRedirecting(false);
        setIsLoggedIn(false);
        setUserData(null);
        setLoggedInUser(null);
      }

      setAuthChecked(true);
    };

    checkAuth();
  }, [
    isHostPathLaunchRoute,
    isOnboardingRoute,
    externalReturnUrl,
    externalReturnRedirectFailed,
    sameOriginReturnPathSegment,
    sameOriginReturnRedirectFailed,
    sameOriginReturnActive,
    performLocalLogout,
  ]);

  useEffect(() => {
    return subscribeAuthLogout(() => {
      void performLocalLogout();
    });
  }, [performLocalLogout]);

  useEffect(() => {
    if (!authChecked || !isLoggedIn || isOnboardingRoute) return;

    let cancelled = false;
    const checkSession = async () => {
      try {
        const response = await fetch(apiUrl("/api/v1/auth/status"), {
          credentials: "include",
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        });
        if (cancelled) return;
        if (response.status === 401) {
          await performLocalLogout();
          return;
        }
        if (!response.ok) return;
        const payload = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (payload?.authenticated === false) {
          await performLocalLogout();
        }
      } catch {
        // Ignore transient network failures; the next poll/focus event will retry.
      }
    };

    void checkSession();
    // Frequent enough that other product tabs (different subdomains, no BroadcastChannel) observe
    // server-side logout via user_logout_events soon after a sibling app logs out.
    const pollMs = 8_000;
    const intervalId = window.setInterval(() => {
      void checkSession();
    }, pollMs);
    const onVisibleOrFocused = () => {
      if (document.visibilityState === "visible") {
        void checkSession();
      }
    };
    const onPageShow = (e) => {
      if (e.persisted) void checkSession();
    };
    const onOnline = () => {
      void checkSession();
    };
    window.addEventListener("focus", onVisibleOrFocused);
    document.addEventListener("visibilitychange", onVisibleOrFocused);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("online", onOnline);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onVisibleOrFocused);
      document.removeEventListener("visibilitychange", onVisibleOrFocused);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("online", onOnline);
    };
  }, [authChecked, isLoggedIn, isOnboardingRoute, performLocalLogout]);

  const didReturnToAppRef = useRef(false);
  useEffect(() => {
    if (!authChecked || !isLoggedIn || didReturnToAppRef.current) return;
    const returnUrl = externalReturnUrl;
    if (!returnUrl || externalReturnRedirectFailed) return;
    didReturnToAppRef.current = true;
    const slugParam = customerKey ? `?slug=${encodeURIComponent(customerKey)}` : "";
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    fetch(apiUrl(`/api/v1/auth/return_code${slugParam}`), {
      method: "POST",
      credentials: "include",
      signal: controller.signal,
      headers: { "Content-Type": "application/json", ...(localStorage.getItem("auth_token") ? { Authorization: `Bearer ${localStorage.getItem("auth_token")}` } : {}) },
    })
      .then(async (res) => {
        if (res.status === 401) {
          await performLocalLogout();
          return null;
        }
        return res.ok ? res.json() : null;
      })
      .then((data) => {
        clearTimeout(timeoutId);
        if (data?.code) {
          const sep = returnUrl.includes("?") ? "&" : "?";
          window.location.replace(`${returnUrl}${sep}code=${encodeURIComponent(data.code)}`);
        } else {
          didReturnToAppRef.current = false;
          setExternalReturnRedirectFailed(true);
        }
      })
      .catch(() => {
        clearTimeout(timeoutId);
        didReturnToAppRef.current = false;
        setExternalReturnRedirectFailed(true);
        setLaunchRedirecting(false);
      });
  }, [authChecked, isLoggedIn, customerKey, externalReturnUrl, externalReturnRedirectFailed, performLocalLogout]);

  const didSameOriginReturnRef = useRef(false);
  useEffect(() => {
    if (!authChecked || !isLoggedIn || didSameOriginReturnRef.current) return;
    if (!sameOriginReturnUrl || !sameOriginReturnActive || sameOriginReturnRedirectFailed) return;
    didSameOriginReturnRef.current = true;
    try {
      const current = new URL(window.location.href);
      const target = new URL(sameOriginReturnUrl);
      if (current.toString() === target.toString()) {
        didSameOriginReturnRef.current = false;
        return;
      }
      window.location.replace(target.toString());
    } catch {
      didSameOriginReturnRef.current = false;
    }
  }, [
    authChecked,
    isLoggedIn,
    sameOriginReturnUrl,
    sameOriginReturnActive,
    sameOriginReturnPathSegment,
    sameOriginReturnRedirectFailed,
    launchPathSegment,
  ]);

  const didHostPathRedirectRef = useRef(false);
  useEffect(() => {
    if (!authChecked || !isLoggedIn || didHostPathRedirectRef.current) return;
    if (isOnboardingRoute || typeof window === "undefined") return;
    const segment = launchPathSegment;
    if (!segment || segment === "logout") return;
    const authToken = localStorage.getItem("auth_token");

    didHostPathRedirectRef.current = true;
    const headers = {
      "Content-Type": "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    };
    fetch(apiUrl(`/api/v1/customers/${encodeURIComponent(customerKey || "")}/apps`), {
      credentials: "include",
      headers,
    })
      .then(async (res) => {
        if (res.status === 401) {
          await performLocalLogout();
          return null;
        }
        return res.ok ? res.json() : null;
      })
      .then((data) => {
        const apps = Array.isArray(data?.apps) ? data.apps : [];
        const match = apps.find((app) => {
          const appSlug = String(app?.slug || "").toLowerCase();
          const appId = String(app?.id || "").toLowerCase();
          const target = segment.toLowerCase();
          return appSlug === target || appId === target;
        });
        const launchUrl = String(match?.launchUrl || "").trim();
        if (!launchUrl) {
          didHostPathRedirectRef.current = false;
          setHostPathLaunchUnavailable(true);
          setLaunchRedirecting(false);
          return;
        }
        return fetch(apiUrl("/api/v1/auth/return_code"), {
          method: "POST",
          credentials: "include",
          headers,
        })
          .then(async (res) => {
            if (res.status === 401) {
              await performLocalLogout();
              return null;
            }
            return res.ok ? res.json() : null;
          })
          .then((payload) => {
            const code = String(payload?.code || "").trim();
            if (!code) {
              didHostPathRedirectRef.current = false;
              setHostPathLaunchUnavailable(true);
              setLaunchRedirecting(false);
              return;
            }
            localStorage.setItem(
              LAST_LAUNCH_URL_KEY,
              sanitizeUrlStrippingAuthCallbackParams(launchUrl)
            );
            const sep = launchUrl.includes("?") ? "&" : "?";
            window.location.replace(`${launchUrl}${sep}code=${encodeURIComponent(code)}`);
          });
      })
      .catch(() => {
        didHostPathRedirectRef.current = false;
        setHostPathLaunchUnavailable(true);
        setLaunchRedirecting(false);
      });
  }, [authChecked, isLoggedIn, customerKey, isOnboardingRoute, launchPathSegment, performLocalLogout]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const fetchedBrand = await fetchCustomerBranding();
        if (!mounted) return;
        setBrand(fetchedBrand);
      } catch {
        if (!mounted) return;
        const fallback = { name: "One Door Platform", logoSrc: defaultCustomerLogo, alt: "ODP", product_alias: "ODP Cloud" };
        setBrand(fallback);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [customerKey]);

  if (!authChecked) {
    return (
      <div className="relative h-screen w-screen overflow-hidden bg-white font-sans text-slate-700 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700 mx-auto"></div>
          <p className="mt-4 text-slate-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (isOnboardingRoute) {
    return <OnboardingProvisioningPage brand={brand} />;
  }

  if (isAcceptInviteRoute) {
    return (
      <div className="min-h-screen w-screen bg-white font-sans text-slate-700 flex flex-col">
        <div className="hidden md:block absolute inset-0">
          <div className="absolute inset-y-0 left-0 w-[70%] bg-white" />
          <div className="absolute inset-y-0 right-0 w-[30%] bg-slate-100" />
        </div>
        <header className="relative z-10 flex items-start justify-between px-6 pt-6 flex-shrink-0" role="banner">
          <LogoOneDoor />
        </header>
        <main className="relative z-10 flex-1 flex w-full items-center justify-center px-6 pt-12 pb-8" role="main">
          <AcceptInvitePage brand={brand} />
        </main>
      </div>
    );
  }

  if (
    isLoggedIn &&
    (
          launchRedirecting ||
          (isHostPathLaunchRoute && !hostPathLaunchUnavailable) ||
          (!!externalReturnUrl && !externalReturnRedirectFailed) ||
          sameOriginReturnActive
        )
      ) {
    return (
      <div className="relative h-screen w-screen overflow-hidden bg-white font-sans text-slate-700 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700 mx-auto"></div>
          <p className="mt-4 text-slate-600">Redirecting you to your app...</p>
        </div>
      </div>
    );
  }

  if (isLoggedIn && isHostPathLaunchRoute && hostPathLaunchUnavailable) {
    const envHint = customerEnvLabelHint(customerFromPath);
    return (
      <div className="min-h-screen w-screen bg-white font-sans text-slate-700 flex flex-col items-center justify-center px-6">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-lg font-semibold text-slate-800">App launch unavailable</h1>
          <p className="text-slate-600 text-sm leading-relaxed">
            {envHint ||
              `We could not open “${launchPathSegment}”. It may be disabled, missing a launch URL, or not assigned to your account.`}
          </p>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
            onClick={() => {
              window.history.replaceState(null, "", "/");
              window.location.assign("/");
            }}
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  if (isLoggedIn) {
    return (
      <Dashboard
        brand={brand}
        customerKey={customerKey}
        onLogout={handleLogout}
        usernameOrEmail={loggedInUser}
      />
    );
  }

  return (
    <div className="min-h-screen w-screen bg-white font-sans text-slate-700 flex flex-col">
      <div className="hidden md:block absolute inset-0">
        <div className="absolute inset-y-0 left-0 w-[70%] bg-white" />
        <div className="absolute inset-y-0 right-0 w-[30%] bg-slate-100" />
      </div>

      <header className="relative z-10 flex items-start justify-between px-6 pt-6 flex-shrink-0" role="banner">
        <LogoOneDoor />
        <nav className="hidden md:block" aria-label="Language selection">
          <LanguageDropdown value={language} onChange={setLanguage} />
        </nav>
      </header>

      <main id="main-content" className="relative z-10 flex-1 flex w-full items-center justify-start md:justify-center px-6 pt-12 pb-8 md:px-12 md:py-12" role="main">
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 w-full">
          <CustomerBranding
            logoSrc={customerLogoSrc}
            alt={brand.alt}
            name={brand.name}
          />
          <SSOLoginCard
            brand={brand}
            customerKey={customerKey}
            onLogin={handleLogin}
            onError={(error) => {
              if (error === 'logged_out') {
                performLocalLogout();
              }
            }}
          />
        </div>
      </main>

      <Footer />
    </div>
  );
}
